import * as Sentry from "@sentry/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import toast from "react-hot-toast"
import { RRule } from "rrule"
import { useShallow } from "zustand/react/shallow"

import { setQuestActive, setQuestInactive } from "./api"
import { Toasts } from "../../components/toaster/Toaster"
import { QUERY_KEYS } from "../../constants"
import useMarkMissionComplete from "../../hooks/useMarkMissionComplete"
import i18n from "../../locales/i18n"
import {
  CoachUpsellModalType,
  useCoachUpsellModalStore,
} from "../../pages/care/CoachUpsellModal"
import useCareTeam from "../../pages/care/useCareTeam"
import { useAddEditQuestStore } from "../../pages/questBoard/addEditQuestModal/useAddEditQuestStore"
import { useQBDatePickerStore } from "../../pages/questBoard/QuestBoardDatePicker"
import { useQuestGroupSeries } from "../../pages/questBoard/useQuestGroups"
import { MissionScene } from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { queryClient } from "../../util/queryClient"
import { localStorageItems } from "../../util/storage"
import { convertToByWeekday } from "../../util/util"
import {
  addQuest,
  deleteQuest,
  deleteQuestTemplate,
  editQuest,
  getVerifiedQuests,
  redeemQuest,
  reorderQuests,
} from "../quests"

export const useDeleteQuestTemplateMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteQuestTemplate,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.QUEST_TEMPLATES],
      })
    },
  })
}

export const useEditQuestMutation = () => {
  const {
    questName,
    description,
    photoFile,
    photoUrl,
    coinRewardsMap,
    category,
    routine,
    repetition,
    repeatsFrequency,
    isOngoing,
    startDate,
    reminderTimeMap,
    minutes,
    seconds,
    timerLength,
    assignedUsers,
    mandatory,
    reqReview,
    reqNotes,
    reqPhoto,
    maxAutoVerifyCount,
    completionCriteriaNotes,
    completionCriteriaPhotoFile,
    completionCriteriaPhotoUrl,
    saveTemplate,
    isGrouped,

    resetQuestDetails,
    onClose,
    setErrors,
    selectedQuest,
  } = useAddEditQuestStore(
    useShallow((state) => ({
      questName: state.questName,
      description: state.description,
      photoFile: state.photoFile,
      photoUrl: state.photoUrl,
      coinRewardsMap: state.coinRewardsMap,
      category: state.category,
      routine: state.routine,
      repetition: state.repetition,
      repeatsFrequency: state.repeatsFrequency,
      isOngoing: state.isOngoing,
      startDate: state.startDate,
      reminderTimeMap: state.reminderTimeMap,
      minutes: state.minutes,
      seconds: state.seconds,
      timerLength: state.timerLength,
      assignedUsers: state.assignedUsers,
      mandatory: state.mandatory,
      reqReview: state.reqReview,
      reqNotes: state.reqNotes,
      reqPhoto: state.reqPhoto,
      maxAutoVerifyCount: state.maxAutoVerifyCount,
      completionCriteriaNotes: state.completionCriteriaNotes,
      completionCriteriaPhotoFile: state.completionCriteriaPhotoFile,
      completionCriteriaPhotoUrl: state.completionCriteriaPhotoUrl,
      saveTemplate: state.saveTemplate,
      templateId: state.templateId,
      customTemplateId: state.customTemplateId,
      isGrouped: state.isGrouped,

      resetQuestDetails: state.resetQuestDetails,
      setErrors: state.setErrors,
      onClose: state.onClose,
      selectedQuest: state.selectedQuest,
    }))
  )

  const { questGroupSeries } = useQuestGroupSeries(selectedQuest)

  const checkForErrors = () => {
    const tempErrors = {} as any
    if (questName.length === 0) {
      tempErrors.questName = i18n.t("QUEST_ERROR_TITLE")
    }
    if (assignedUsers.length === 0) {
      tempErrors.assignedUsers = i18n.t("QUEST_ERROR_ASSIGN")
    }
    setErrors(tempErrors)
    if (Object.keys(tempErrors)?.length > 0)
      throw new Error("Invalid quest details")
  }

  const editQuestAction = () => {
    if (!selectedQuest || questGroupSeries.length < 1)
      throw new Error("No quest selected")
    checkForErrors()

    const isRepeatingQuest = !!repetition?.some((day) => day)

    const recurrence = new RRule({
      freq: isRepeatingQuest ? RRule.WEEKLY : RRule.DAILY,
      byweekday: isRepeatingQuest ? convertToByWeekday(repetition) : undefined,
      interval: isRepeatingQuest ? repeatsFrequency ?? 1 : undefined,
    })

    const isSavingWithoutPhoto = photoFile === null && photoUrl === null
    const isSavingWithoutCompletionCriteriaPhoto =
      completionCriteriaPhotoFile === null &&
      completionCriteriaPhotoUrl === null

    const timer_length =
      (!minutes || minutes.value === "0") && (!seconds || seconds.value === "0")
        ? null
        : timerLength

    const editQuestPromises = [] as Promise<any>[]

    questGroupSeries.forEach((questSeries, index) => {
      const userId = assignedUsers[index]

      const questInfo = {
        title: questName,
        ...(isSavingWithoutPhoto ? { photo_url: null } : {}),
        requires_review: reqReview,
        requires_completion_notes: reqNotes,
        requires_completion_photo: reqPhoto,
        photo_auto_verify_max: maxAutoVerifyCount,
        completion_criteria_notes: completionCriteriaNotes,
        ...(isSavingWithoutCompletionCriteriaPhoto
          ? { completion_criteria_photo_url: null }
          : {}),
        routine: routine,
        save_as_template: saveTemplate,
        mandatory: mandatory,
        redeemable_reward: coinRewardsMap[userId],
        description,
        occurrence_limit: isRepeatingQuest ? null : 1,
        skill: category,
        timer_length,
        reminder_time: reminderTimeMap[routine] || null,
        recurrence: recurrence.toString(),
        start_date: isOngoing
          ? dayjs().format("YYYY-MM-DD")
          : dayjs(startDate || new Date()).format("YYYY-MM-DD"),
        end_date:
          isOngoing || isRepeatingQuest
            ? null
            : dayjs(startDate || new Date()).format("YYYY-MM-DD"),
        quest_group_id:
          isGrouped && assignedUsers.length > 1
            ? questSeries.quest_group_id
            : null,
      }

      editQuestPromises.push(
        editQuest(
          questSeries.id,
          userId,
          questInfo,
          photoFile ?? undefined,
          completionCriteriaPhotoFile ?? undefined
        )
      )
    })

    return Promise.all(editQuestPromises)
  }

  const editQuestMutation = useMutation({
    mutationFn: editQuestAction,
    onError: (err: any) => {
      console.log(err)
      Sentry.captureException(err)
      toast.error("Error editing quest. Please refresh the page and try again.")
    },
    onSuccess: async () => {
      trackAnalyticEvent(ANALYTIC_EVENTS.EDIT_QUEST)
      await queryClient.invalidateQueries([QUERY_KEYS.QUEST_BOARD])
      queryClient.invalidateQueries([QUERY_KEYS.ALL_QUESTS])
      onClose()
      resetQuestDetails()
    },
  })

  return editQuestMutation
}

export const useAddQuestMutation = () => {
  const {
    questName,
    description,
    photoFile,
    coinRewardsMap,
    category,
    routine,
    repetition,
    repeatsFrequency,
    isOngoing,
    startDate,
    reminderTimeMap,
    timerLength,
    assignedUsers,
    mandatory,
    reqReview,
    reqNotes,
    reqPhoto,
    maxAutoVerifyCount,
    completionCriteriaNotes,
    completionCriteriaPhotoFile,
    saveTemplate,
    isGrouped,
    templateId,
    customTemplateId,

    resetQuestDetails,
    onClose,
    setErrors,
  } = useAddEditQuestStore(
    useShallow((state) => ({
      questName: state.questName,
      description: state.description,
      photoFile: state.photoFile,
      coinRewardsMap: state.coinRewardsMap,
      category: state.category,
      routine: state.routine,
      repetition: state.repetition,
      repeatsFrequency: state.repeatsFrequency,
      isOngoing: state.isOngoing,
      startDate: state.startDate,
      reminderTimeMap: state.reminderTimeMap,
      timerLength: state.timerLength,
      assignedUsers: state.assignedUsers,
      mandatory: state.mandatory,
      reqReview: state.reqReview,
      reqNotes: state.reqNotes,
      reqPhoto: state.reqPhoto,
      maxAutoVerifyCount: state.maxAutoVerifyCount,
      completionCriteriaNotes: state.completionCriteriaNotes,
      completionCriteriaPhotoFile: state.completionCriteriaPhotoFile,
      saveTemplate: state.saveTemplate,
      templateId: state.templateId,
      customTemplateId: state.customTemplateId,
      isGrouped: state.isGrouped,

      resetQuestDetails: state.resetQuestDetails,
      setErrors: state.setErrors,
      onClose: state.onClose,
      selectedQuest: state.selectedQuest,
    }))
  )
  const markMissionComplete = useMarkMissionComplete(MissionScene.QUEST_TAB)

  const checkForErrors = () => {
    const tempErrors = {} as any
    if (questName.length === 0) {
      tempErrors.questName = i18n.t("QUEST_ERROR_TITLE")
    }
    if (assignedUsers.length === 0) {
      tempErrors.assignedUsers = i18n.t("QUEST_ERROR_ASSIGN")
    }
    setErrors(tempErrors)
    if (Object.keys(tempErrors)?.length > 0)
      throw new Error("Invalid quest details")
  }

  const addQuestAction = (hasParentSelected?: boolean) => {
    checkForErrors()

    const isRepeatingQuest = !!repetition.some((day) => day)

    const recurrence = new RRule({
      freq: isRepeatingQuest ? RRule.WEEKLY : RRule.DAILY,
      byweekday: isRepeatingQuest ? convertToByWeekday(repetition) : undefined,
      interval: isRepeatingQuest ? repeatsFrequency ?? 1 : undefined,
    })

    const addQuestPromises = [] as Promise<any>[]

    const sharedData = {
      title: questName,
      requires_review: reqReview,
      requires_completion_notes: reqNotes,
      requires_completion_photo: reqPhoto,
      photo_auto_verify_max: maxAutoVerifyCount,
      completion_criteria_notes: completionCriteriaNotes,
      routine: routine,
      mandatory: mandatory,
      description,
      skill: category,
      timer_length: timerLength,
      reminder_time: reminderTimeMap[routine] || null,
      occurrence_limit: isRepeatingQuest ? null : 1,
      template_id: templateId,
      custom_template_id: customTemplateId,
      recurrence: recurrence.toString(),
      start_date: isOngoing
        ? dayjs().format("YYYY-MM-DD")
        : dayjs(startDate || new Date()).format("YYYY-MM-DD"),
      end_date:
        isOngoing || isRepeatingQuest
          ? null
          : dayjs(startDate || new Date()).format("YYYY-MM-DD"),
    }

    // if grouped quest, only hit endpoint once. Do not send user_id or redeemable_reward
    if (isGrouped && assignedUsers.length > 1) {
      addQuestPromises.push(
        addQuest(
          undefined,
          {
            ...sharedData,
            quest_group_user_rewards: assignedUsers.map((userId) => ({
              user_id: userId,
              redeemable_reward: coinRewardsMap[userId],
            })),
            save_as_template: saveTemplate,
          },
          photoFile ?? undefined,
          completionCriteriaPhotoFile ?? undefined
        )
      )
    } else {
      // For each user, create a quest promise
      assignedUsers.forEach((userId, index) => {
        addQuestPromises.push(
          addQuest(
            userId,
            {
              ...sharedData,
              user_id: userId,
              redeemable_reward: coinRewardsMap[userId],
              ...(index === 0 ? { save_as_template: saveTemplate } : {}),
            },
            photoFile ?? undefined,
            completionCriteriaPhotoFile ?? undefined
          )
        )
      })
    }

    trackAnalyticEvent(ANALYTIC_EVENTS.ADD_QUEST, {
      is_parent_quest: !!hasParentSelected,
    })

    // Wait for all the API calls to complete before continuing
    return Promise.all(addQuestPromises)
  }

  const addQuestMutation = useMutation({
    mutationFn: addQuestAction,
    onError: (err: any) => {
      console.log(err)
      Sentry.captureException(err)
    },
    onSuccess: async () => {
      if (localStorage.getItem(localStorageItems.needsToRefreshQuests)) {
        // Fixes issue where first quest created after acc creation was not appearing
        queryClient.removeQueries([QUERY_KEYS.QUEST_BOARD])
        useQBDatePickerStore.getState().triggerRefresh()
        localStorage.removeItem(localStorageItems.needsToRefreshQuests)
      } else {
        await queryClient.invalidateQueries([QUERY_KEYS.QUEST_BOARD])
      }
      queryClient.invalidateQueries([QUERY_KEYS.PARENT_TASKS])
      queryClient.invalidateQueries([QUERY_KEYS.ALL_QUESTS])
      markMissionComplete()

      onClose()
      resetQuestDetails()
    },
  })

  return addQuestMutation
}

export const useRedeemAllQuestRewardsMutation = () => {
  return useMutation({
    mutationFn: async ({ userId }: { userId: number }) => {
      const verifiedQuests = await getVerifiedQuests(userId)
      const redeemAllPromises = verifiedQuests.map((questInstance) =>
        redeemQuest({ questInstanceId: questInstance.id })
      )
      return Promise.all(redeemAllPromises)
    },
    onSuccess: async () => {
      const promises = [
        queryClient.invalidateQueries([QUERY_KEYS.FAMILY]),
        queryClient.invalidateQueries([QUERY_KEYS.COINS_READY_TO_REDEEM]),
        queryClient.invalidateQueries([QUERY_KEYS.COIN_CUMULATIVE_STATS]),
        queryClient.invalidateQueries([QUERY_KEYS.COIN_TRANSACTIONS]),
        queryClient.invalidateQueries([QUERY_KEYS.QUEST_BOARD]),
      ]
      await Promise.all(promises)
    },
  })
}

export const useSetQuestInactiveMutation = () => {
  const questBoardDate = useQBDatePickerStore(
    useShallow((state) => state.questBoardDate)
  )
  const questBoardQueryKey = [
    QUERY_KEYS.QUEST_BOARD,
    dayjs(questBoardDate).format("YYYY-MM-DD"),
  ]

  return useMutation({
    mutationFn: (questSeriesIds: number[]) =>
      Promise.all(questSeriesIds.map((id) => setQuestInactive(id))),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: questBoardQueryKey }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_QUESTS] }),
      ])
      trackAnalyticEvent(ANALYTIC_EVENTS.INACTIVATE_QUESTS)
    },
  })
}

export const useSetQuestActiveMutation = () => {
  const questBoardDate = useQBDatePickerStore(
    useShallow((state) => state.questBoardDate)
  )
  const questBoardQueryKey = [
    QUERY_KEYS.QUEST_BOARD,
    dayjs(questBoardDate).format("YYYY-MM-DD"),
  ]
  return useMutation({
    mutationFn: (questSeriesIds: number[]) =>
      Promise.all(questSeriesIds.map((id) => setQuestActive(id))),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: questBoardQueryKey }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_QUESTS] }),
      ])
      trackAnalyticEvent(ANALYTIC_EVENTS.ACTIVATE_QUESTS)
    },
  })
}

export const useDeleteQuestsMutation = () => {
  const { hasCoach } = useCareTeam()
  const { onOpen: openCoachUpsellModal } = useCoachUpsellModalStore()
  const { questBoardDate } = useQBDatePickerStore()

  const queryClient = useQueryClient()

  const questBoardQueryKey = [
    QUERY_KEYS.QUEST_BOARD,
    dayjs(questBoardDate).format("YYYY-MM-DD"),
  ]

  return useMutation({
    mutationFn: (questSeriesIds: number[]) => {
      const deleteQuestPromises = questSeriesIds.map(
        (questId) => questId && deleteQuest(questId)
      )
      return Promise.all(deleteQuestPromises)
    },
    onSuccess: async (_, questSeriesIds) => {
      Promise.all([
        queryClient.invalidateQueries(questBoardQueryKey),
        queryClient.invalidateQueries([QUERY_KEYS.ALL_QUESTS]),
      ])

      toast.success(`Quest${questSeriesIds.length > 1 ? "s" : ""} deleted`)

      // Show coach upsell modal if needed
      if (hasCoach) return
      const lastSeenUpsellModalDate = localStorage.getItem(
        localStorageItems.deleteCoachUpsellModalSeenDate
      )
      const needsToSeeDeleteCoachUpsellModal =
        !dayjs().isSame(dayjs(lastSeenUpsellModalDate), "week") ||
        !lastSeenUpsellModalDate
      if (needsToSeeDeleteCoachUpsellModal)
        openCoachUpsellModal(CoachUpsellModalType.delete)
    },
    onError: () => {
      toast.error("Problem deleting quests. Please refresh and try again.")
    },
  })
}

export const useReorderQuestsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: reorderQuests,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ALL_QUESTS])
    },
    onError: (error) => {
      console.error("Error reordering quests:", error)
      toast.error(Toasts.errorReorderingQuests, { duration: 1500 })
    },
  })
}
